/**
 * Overrides attachShadow to inject custom styles into gmp-place-autocomplete shadow DOM.
 * This allows styling of the internal elements within the web component.
 * @returns {Function} Cleanup function to restore original attachShadow method.
 */
export const setupShadowDOMOverride = () => {
  // Store the original attachShadow method.
  const attachShadow = Element.prototype.attachShadow;

  // Override attachShadow to intercept gmp-place-autocomplete element creation.
  Element.prototype.attachShadow = function (/** @type {any} */ init) {
    // Check if we are the Google places autocomplete element.
    if (this.localName === 'gmp-place-autocomplete') {
      // Override the default behaviour to set mode to open and inject styles.
      const shadow = attachShadow.call(this, {
        ...init,
        mode: 'open'
      });

      // Inline SVG as data URL to avoid path resolution issues in shadow DOM.
      // This is the most reliable approach for shadow DOM contexts.
      const searchIconSvg = '<svg class="icon icon-search" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 20 20"><path fill="#171717" fill-rule="evenodd" d="M9.167 1.25a7.917 7.917 0 1 0 5.295 13.802l3.577 3.576a.417.417 0 0 0 .589-.59l-3.576-3.576A7.917 7.917 0 0 0 9.167 1.25ZM2.083 9.167a7.083 7.083 0 1 1 14.167 0 7.083 7.083 0 0 1-14.167 0Z" clip-rule="evenodd"/></svg>';
      const searchIconDataUrl = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(searchIconSvg);

      const cancelIconSvg = '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M8.85431 8.14647C8.65905 7.95119 8.34247 7.95118 8.1472 8.14643C7.95193 8.34168 7.95191 8.65826 8.14716 8.85353L11.2937 12.0004L8.14791 15.1465C7.95266 15.3417 7.95267 15.6583 8.14795 15.8536C8.34322 16.0488 8.6598 16.0488 8.85505 15.8535L12.0007 12.7075L15.1464 15.8535C15.3417 16.0488 15.6582 16.0488 15.8535 15.8536C16.0488 15.6583 16.0488 15.3417 15.8535 15.1465L12.7078 12.0004L15.8543 8.85354C16.0496 8.65826 16.0496 8.34168 15.8543 8.14643C15.659 7.95118 15.3425 7.95119 15.1472 8.14646L12.0007 11.2932L8.85431 8.14647Z" fill="#171717"/><path fill-rule="evenodd" clip-rule="evenodd" d="M12 1.5C6.20101 1.5 1.5 6.20101 1.5 12C1.5 17.7989 6.20101 22.5 12 22.5C17.7989 22.5 22.5 17.7989 22.5 12C22.5 6.20101 17.7989 1.5 12 1.5ZM2.5 12C2.5 6.75329 6.75329 2.5 12 2.5C17.2467 2.5 21.5 6.75329 21.5 12C21.5 17.2467 17.2467 21.5 12 21.5C6.75329 21.5 2.5 17.2467 2.5 12Z" fill="#171717"/></svg>';
      const cancelIconDataUrl = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(cancelIconSvg);

      const style = document.createElement('style');
      // Apply custom styles to match the existing design system.
      style.textContent = `
        .widget-container {
          border: none !important;
        }
        .input-container {
          padding: 0 !important;
        }
        .focus-ring {
          display: none !important;
        }
        .autocomplete-icon,
        .clear-button {
          width: 2rem !important;
        }
        .autocomplete-icon {
          left: -2.5rem !important;
          position: absolute !important;
        }
        .autocomplete-icon::before {
          content: '';
          width: 100%;
          display: flex;
          height: 4.4rem;
          background: url('${searchIconDataUrl}') no-repeat center;
          background-size: 1.8rem;
          position: absolute;
          left: -0.4rem;
        }
        .autocomplete-icon svg path,
        .clear-button svg path {
          fill: var(--color-white) !important;
        }
       .clear-button::before {
          content: '';
          width: 2.2rem;
          display: flex;
          height: 4.4rem;
          background: url('${cancelIconDataUrl}') no-repeat center;
          background-size: contain;
          position: absolute;
          right: -0.4rem;
        }
        input {
          --tw-text-opacity: 1;
          color: rgb(0 0 0 / var(--color-black));
          background-color: transparent;
          font-size: var(--text-lg) !important;
        }
        .dropdown> ul > li:hover {
          background-color: var(--color-orange) !important;
        }
        .dropdown ul li:hover .place-autocomplete-element-place-name,
        .dropdown ul li:hover .place-autocomplete-element-place-details {
          color: var(--color-white) !important;
        }
        .dropdown {
          border-radius: 0.8rem !important;
          font-color: var(--color-black) !important;
          border-top: 0.1rem solid #e0e0e0 !important;
          box-shadow: 0 0.4rem 1rem rgba(0, 0, 0, 0.1) !important;
          background-color: var(--color-white) !important;
          overflow: hidden !important;
        }
        .place-autocomplete-element-row .place-autocomplete-element-place-name {
          color: var(--color-black) !important;
        }
        .input-container button.clear-button:hover {
          background-color: transparent !important;
        }
      `;

      shadow.appendChild(style);
      // Return the shadow root with our styles injected.
      return shadow;
    }
    // For other elements, proceed with the original behaviour.
    return attachShadow.call(this, init);
  };

  // Return cleanup function to restore original attachShadow.
  return () => {
    Element.prototype.attachShadow = attachShadow;
  };
};

